import React from 'react';
import { Quote } from '../types';
import { FileCheck } from 'lucide-react';
import { formatCurrency, generateInvoiceNumber } from '../utils/helpers';
import { createEnquiry, isLifecycleConnected, fetchEnquiries, Enquiry } from '../utils/lifecycleApi';

interface QuoteQuickLookProps {
  quote: Quote;
  onClose: () => void;
  onEdit?: (quote: Quote) => void;
  onStatusChange?: (id: string, status: string) => void;
  onConvertToInvoice?: (id: string, invoiceNumber: string) => void;
  onMarkAdvanceReceived?: (id: string) => void;
}

const STATUS_OPTIONS = ['draft', 'sent', 'confirmed', 'cancelled'];
const STATUS_COLORS: Record<string, string> = { draft: 'badge-amber', sent: 'badge-teal', confirmed: 'badge-best', cancelled: 'badge-red' };

function tierBadgeClass(tier: string): string {
  if (tier === 'Best') return 'badge-best';
  if (tier === 'Luxe') return 'badge-luxe';
  if (tier === 'Elite') return 'badge-elite';
  return 'badge-amber'; // General Planner / unknown
}

// Shared quick-look preview used from both the Dashboard's Recent Quotes table
// and Quote History. Lets Shivam glance at a quote and change its status
// without committing to opening the full 8-step edit wizard — "Edit Full
// Quote" is one click away if he does need it.
function printQuote(quote: any, mode: 'studio' | 'customer') {
  const isCustomer = mode === 'customer';
  const lines: string[] = [];
  const pkg = (quote.packageItems || [])[0];
  lines.push(`<h2>Shivam Studio — ${isCustomer ? 'Quotation' : 'Studio Copy'}</h2>`);
  lines.push(`<p><strong>${quote.customerName}</strong> · ${quote.customerPhone} · ${quote.eventName}</p>`);
  lines.push(`<p>Quote #: ${quote.quoteNumber} &nbsp;|&nbsp; Date: ${new Date(quote.createdAt).toLocaleDateString('en-IN')}</p><hr/>`);
  if (pkg) {
    lines.push(`<h3>${pkg.serviceName} — ${pkg.productName} (${pkg.qualityTier})</h3>`);
    if (!isCustomer) {
      (pkg.contractors || []).forEach((c: any) => {
        const days = Math.max(1, (pkg.eventDates || []).filter((d: any) => d.date).length);
        lines.push(`<p>${c.contractor} — ${c.deliverable}${(c.qty||1)>1?` × ${c.qty}`:''} × ${days} day(s) &nbsp; <strong>₹${(c.rate*(c.qty||1)*days).toLocaleString('en-IN')}</strong></p>`);
      });
      (pkg.ppVendors || []).forEach((v: any) => {
        lines.push(`<p>${v.vendor} — ${v.deliverable}${(v.qty||1)>1?` × ${v.qty}`:''} &nbsp; <strong>₹${(v.rate*(v.qty||1)).toLocaleString('en-IN')}</strong></p>`);
      });
      if (pkg.album) {
        lines.push(`<p>Album: ${pkg.album.vendorName} &nbsp; <strong>₹${(pkg.album.totalAlbumCost).toLocaleString('en-IN')}</strong></p>`);
      }
      (pkg.addOns || []).forEach((a: any) => {
        lines.push(`<p>+ ${a.name}${(a.qty||1)>1?` × ${a.qty}`:''} &nbsp; <strong>₹${(a.cost*(a.qty||1)).toLocaleString('en-IN')}</strong></p>`);
      });
    } else {
      // Customer mode: list services/deliverables but no individual prices
      lines.push('<ul>');
      (pkg.contractors || []).forEach((c: any) => lines.push(`<li>${c.deliverable}</li>`));
      (pkg.ppVendors || []).forEach((v: any) => lines.push(`<li>${v.deliverable}</li>`));
      if (pkg.album) lines.push(`<li>Album: ${pkg.album.vendorName}</li>`);
      (pkg.addOns || []).forEach((a: any) => lines.push(`<li>${a.name}${(a.qty||1)>1?` × ${a.qty}`:''}</li>`));
      lines.push('</ul>');
    }
  }
  lines.push(`<hr/><h3>Total Quoted Amount: ₹${(quote.quotedAmount||0).toLocaleString('en-IN')}</h3>`);
  if (!isCustomer) lines.push(`<p style="color:#888;font-size:12px">This is a studio copy with detailed pricing. Not for customer distribution.</p>`);
  lines.push('<p style="color:#888;font-size:12px;margin-top:20px">Shivam Studio · planner.shivamstudio.com</p>');
  const html = `<!DOCTYPE html><html><head><title>Quote ${quote.quoteNumber}</title>
    <style>body{font-family:Arial,sans-serif;padding:32px;max-width:700px;margin:auto}h2{color:#1F2937}h3{color:#B8860B}hr{border:1px solid #eee}p,li{margin:6px 0;font-size:14px}</style>
    </head><body>${lines.join('\n')}</body></html>`;
  const w = window.open('','_blank');
  if(w){w.document.write(html);w.document.close();w.print();}
}


async function addToLifecycle(quote: any): Promise<string> {
  if (!isLifecycleConnected()) throw new Error('Not connected to Lifecycle backend');
  const pkg = (quote.packageItems || [])[0];
  const serviceKey = pkg?.serviceKey || 'wedding';
  const serviceMap: Record<string,string> = {
    wedding:'Wedding', pre_wedding:'Pre-Wedding', engagement:'Engagement',
    anniversary:'Anniversary', birthday:'Birthday', office_event:'Office Event', any_event:'Any Event'
  };
  const serviceType = serviceMap[serviceKey] || 'Wedding';
  const eventDatesStr = (pkg?.eventDates || []).filter((d: any) => d.date).map((d: any) => d.date).join(', ');
  const enquiry = await createEnquiry({
    service_type: serviceType,
    customer_name: quote.customerName || '',
    contact_number: quote.customerPhone || '',
    email: '',
    contact_via: 'Phone Call',
    event_dates: eventDatesStr,
    location_venue: '',
    suggested_package: pkg ? `${pkg.productName} (${pkg.qualityTier})` : '',
    follow_up_date: null,
    follow_up_status: 'Pending',
    overall_status: 'Open',
    comments: `Auto-created from planner quote ${quote.quoteNumber}. Quoted amount: ₹${(quote.quotedAmount||0).toLocaleString('en-IN')}.`,
  });
  return enquiry.ref_number;
}

const SVCS = ['Wedding','Pre-Wedding','Engagement','Anniversary','Birthday','Office Event','Any Event'];

// Feature #1: freehand quote↔enquiry linking. Stored separately from
// ss_booking_quote_links (which only exists once a booking is created) —
// this survives the gap between "both exist, made separately" and
// "booking actually created", and Enquiry Tracker's Create Booking modal
// picks it up automatically to pre-select the right quote.
function getQuoteEnquiryLink(quoteId: string): { enquiryId: number; ref_number: string; service_type: string; customer_name: string } | null {
  try {
    const links = JSON.parse(localStorage.getItem('ss_quote_enquiry_links') || '{}');
    return links[quoteId] || null;
  } catch { return null; }
}
function setQuoteEnquiryLink(quoteId: string, link: { enquiryId: number; ref_number: string; service_type: string; customer_name: string }) {
  const links = JSON.parse(localStorage.getItem('ss_quote_enquiry_links') || '{}');
  links[quoteId] = link;
  localStorage.setItem('ss_quote_enquiry_links', JSON.stringify(links));
}

export default function QuoteQuickLook({ quote, onClose, onEdit, onStatusChange, onConvertToInvoice, onMarkAdvanceReceived }: QuoteQuickLookProps) {
  const [lifecycleStatus, setLifecycleStatus] = React.useState<'idle'|'loading'|'done'|'error'>('idle');
  const [lifecycleRef, setLifecycleRef] = React.useState('');
  const [lifecycleMsg, setLifecycleMsg] = React.useState('');
  // Alternative path — link to an enquiry that already exists instead of
  // creating a new one. 'choose' shows the Create-New vs Link-Existing
  // buttons; 'search' shows the picker.
  const [linkMode, setLinkMode] = React.useState<'choose'|'search'|'linked'>(() => getQuoteEnquiryLink(quote.id) ? 'linked' : 'choose');
  const [existingLink] = React.useState(() => getQuoteEnquiryLink(quote.id));
  const [enquirySearch, setEnquirySearch] = React.useState('');
  const [enquiryList, setEnquiryList] = React.useState<Enquiry[]>([]);
  const [enquiryLoading, setEnquiryLoading] = React.useState(false);
  const [enquiryLoadError, setEnquiryLoadError] = React.useState('');
  const [enqTab, setEnqTab] = React.useState('Wedding');

  // Loads ONE service at a time — same reliable pattern as Enquiry Tracker,
  // instead of firing all 7 services at once.
  const openLinkSearch = async (service: string = 'Wedding') => {
    setLinkMode('search');
    setEnqTab(service);
    setEnquiryLoading(true);
    setEnquiryLoadError('');
    try {
      const list = await fetchEnquiries(service);
      setEnquiryList(list.filter((e: any) => e.overall_status !== 'Lost'));
    } catch (e: any) {
      setEnquiryLoadError(e.message || 'Could not load enquiries');
      setEnquiryList([]);
    } finally {
      setEnquiryLoading(false);
    }
  };

  const handleLinkExisting = (e: Enquiry) => {
    setQuoteEnquiryLink(quote.id, { enquiryId: e.id, ref_number: e.ref_number, service_type: e.service_type, customer_name: e.customer_name || '' });
    setLinkMode('linked');
  };

  const handleAddToLifecycle = async () => {
    setLifecycleStatus('loading');
    setLifecycleMsg('');
    try {
      const ref = await addToLifecycle(quote);
      setLifecycleRef(ref);
      setLifecycleStatus('done');
      setLifecycleMsg(`Enquiry created: ${ref} — Go to Enquiry Tracker to continue`);
    } catch (e: any) {
      setLifecycleStatus('error');
      setLifecycleMsg(e.message || 'Failed to create enquiry');
    }
  };
  const isGP = !!(quote as any).isGeneralPlanner;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <h2>{quote.eventName}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{quote.customerName} · {quote.customerPhone}</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'monospace', marginTop: 2 }}>{quote.quoteNumber}</p>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {onConvertToInvoice && (quote as any).documentType !== 'invoice' && (
              <button className="btn btn-secondary btn-sm" style={{fontSize:12,display:'flex',alignItems:'center',gap:4}}
                onClick={() => {
                  const q = quote as any;
                  if (!window.confirm(`Convert ${q.quoteNumber} to Invoice?\n\nThis creates the invoice and marks it ready to send to ${q.customerName}. It does NOT confirm payment — use "Confirm Advance Received" separately once the advance actually comes in.`)) return;
                  const invoiceNum = generateInvoiceNumber();
                  if (onConvertToInvoice) onConvertToInvoice(quote.id, invoiceNum);
                }}>
                <FileCheck size={13} color="var(--teal)" /> Convert to Invoice
              </button>
            )}
            {(quote as any).documentType === 'invoice' && !(quote as any).advanceReceived && (
              <>
                <span style={{fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:4,background:'#FEF3C7',color:'#92400E',border:'1px solid #FCD34D'}}>INVOICE · AWAITING ADVANCE</span>
                {onMarkAdvanceReceived && (
                  <button className="btn btn-secondary btn-sm" style={{fontSize:12,display:'flex',alignItems:'center',gap:4}}
                    onClick={async () => {
                      const q = quote as any;
                      if (!window.confirm(`Confirm advance received from ${q.customerName} for ${q.quoteNumber}?`)) return;
                      onMarkAdvanceReceived(quote.id);
                      if (isLifecycleConnected()) {
                        const go = window.confirm(`Advance confirmed! ✓\n\nCreate booking now to start the lifecycle tracking for ${q.customerName}?`);
                        if (go) {
                          try {
                            const ref = await addToLifecycle(quote);
                            window.alert(`✓ Enquiry created: ${ref}\n\nGo to Enquiry Tracker → find this enquiry → mark Confirmed → Create Booking.`);
                          } catch { window.alert('Advance confirmed. Go to Enquiry Tracker to create the booking.'); }
                        }
                      }
                    }}>
                    <FileCheck size={13} color="var(--gold)" /> Confirm Advance Received
                  </button>
                )}
              </>
            )}
            {(quote as any).documentType === 'invoice' && (quote as any).advanceReceived && (
              <span style={{fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:4,background:'#D1FAE5',color:'#065F46',border:'1px solid #6EE7B7'}}>INVOICE · ADVANCE RECEIVED</span>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Status changer */}
        <div style={{ marginBottom: 20, padding: '12px 14px', background: '#F7FAFC', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Status</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {STATUS_OPTIONS.map(s => {
              const isCurrent = quote.status === s;
              return (
                <button key={s}
                  disabled={!onStatusChange}
                  onClick={() => onStatusChange && onStatusChange(quote.id, s)}
                  className={`badge ${STATUS_COLORS[s]}`}
                  style={{
                    cursor: onStatusChange ? 'pointer' : 'default',
                    border: isCurrent ? '2px solid currentColor' : '1.5px solid transparent',
                    opacity: isCurrent ? 1 : 0.5,
                    fontWeight: isCurrent ? 700 : 500,
                  }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        {quote.packageItems?.map((pkg, pi) => (
          <div key={pi} className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <h4>{pkg.serviceName}</h4>
                <span className={`badge ${tierBadgeClass(pkg.qualityTier)}`}>{pkg.qualityTier}</span>
              </div>
            </div>
            {(() => {
              const days = Math.max(1, (pkg.eventDates || []).filter((d: any) => d.date).length);
              return pkg.contractors?.map((c, ci) => {
                const total = c.rate * (c.qty || 1) * days;
                const label = days > 1
                  ? `${c.contractor} — ${c.deliverable}${(c.qty||1) > 1 ? ` × ${c.qty}` : ''} × ${days} day(s)`
                  : `${c.contractor} — ${c.deliverable}${(c.qty||1) > 1 ? ` × ${c.qty}` : ''}`;
                return (
                  <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>{label}</span>
                    <span style={{ color: 'var(--teal-light)' }}>{formatCurrency(total)}</span>
                  </div>
                );
              });
            })()}
            {pkg.ppVendors?.map((v, vi) => (
              <div key={vi} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>{v.vendor} — {v.deliverable}{v.qty && v.qty > 1 ? ` × ${v.qty}` : ''}</span>
                <span style={{ color: 'var(--blue)' }}>{formatCurrency(v.rate * (v.qty || 1))}</span>
              </div>
            ))}
            {pkg.addOns?.length > 0 && pkg.addOns.map((a: any, ai: number) => (
              <div key={ai} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>+ {a.name}{a.qty && a.qty > 1 ? ` × ${a.qty}` : ''}</span>
                <span style={{ color: 'var(--gold)' }}>{formatCurrency(a.cost * (a.qty || 1))}</span>
              </div>
            ))}
            {pkg.album && (() => {
              // If custom sheet rate was set, recalculate primary album cost at that rate
              const dr = (pkg as any).albumDesignChargePerSheet || 0;
              const bs = pkg.album.sheets || 40;
              const es = (pkg as any).extraAlbumSheets || 0;
              const n = pkg.album.numAlbums || 1;
              const sp = (pkg as any).albumSplitSets || 1;
              const cp = (pkg as any).albumAdditionalCopies || 0;
              const primaryDesign = dr > 0 ? bs * dr * n : 0;
              const extraPaper = es * pkg.album.paperRatePerSheet * n;
              const extraDesign = dr > 0 ? es * dr * n : 0;
              const splitCost = sp > 1 ? (sp - 1) * pkg.album.coverRate * n : 0;
              const oneCopy = pkg.album.totalAlbumCost + primaryDesign + extraPaper + extraDesign;
              const photoEdit = (pkg as any).photoEditCharge;
              const photoEditCost = photoEdit?.enabled ? (photoEdit.rate || 50) * (photoEdit.qty || 0) : 0;
              return (<>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginTop:6 }}>
                  <span>Album ({pkg.album.vendorName})</span>
                  <span style={{ color:'var(--gold)' }}>{formatCurrency(pkg.album.totalAlbumCost)}</span>
                </div>
                {dr > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, paddingLeft:12, color:'var(--text-secondary)' }}>
                    <span>+ Design charge: {bs} sheets × ₹{dr}</span>
                    <span style={{ color:'var(--gold)' }}>+{formatCurrency(primaryDesign)}</span>
                  </div>
                )}
                {es > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, paddingLeft:12, color:'var(--text-secondary)' }}>
                    <span>+ Extra {es} sheets (paper{dr > 0 ? ' + design' : ''})</span>
                    <span style={{ color:'var(--gold)' }}>+{formatCurrency(extraPaper + extraDesign)}</span>
                  </div>
                )}
                {sp > 1 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, paddingLeft:12, color:'var(--text-secondary)' }}>
                    <span>+ Split: {sp} sets</span>
                    <span style={{ color:'var(--gold)' }}>+{formatCurrency(splitCost)}</span>
                  </div>
                )}
                {cp > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, paddingLeft:12, color:'var(--text-secondary)' }}>
                    <span>+ {cp} additional copy(s) × {formatCurrency(oneCopy)}</span>
                    <span style={{ color:'var(--gold)' }}>+{formatCurrency(cp * oneCopy)}</span>
                  </div>
                )}
                {photoEdit?.enabled && photoEdit.qty > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, paddingLeft:12, color:'#0369A1' }}>
                    <span>📸 Photo Editing: {photoEdit.qty} × ₹{photoEdit.rate || 50}/photo</span>
                    <span>+{formatCurrency(photoEditCost)}</span>
                  </div>
                )}
              </>);
            })()}
          </div>
        ))}

        {(quote as any).notes && (
          <div className="card" style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
            <strong style={{ display: 'block', marginBottom: 4, color: 'var(--text-primary)' }}>Notes</strong>
            {(quote as any).notes}
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Quoted Amount</span>
          <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--gold)' }}>{formatCurrency(quote.quotedAmount)}</span>
        </div>

        {/* Print options */}
        <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => printQuote(quote, 'studio')}
            style={{ fontSize:12 }}>🖨 Studio Copy (with prices)</button>
          <button className="btn btn-secondary btn-sm" onClick={() => printQuote(quote, 'customer')}
            style={{ fontSize:12 }}>📄 Customer Copy (total only)</button>
        </div>

        {/* Add to Lifecycle */}
        {(linkMode === 'linked' || existingLink) ? (
          <div style={{ marginTop:12, padding:'10px 14px', background:'rgba(56,178,172,0.08)', border:'1.5px solid var(--teal)', borderRadius:8, fontSize:13, color:'var(--teal)' }}>
            ✓ Linked to existing enquiry: <strong>{existingLink?.ref_number}</strong>{existingLink?.customer_name ? ` — ${existingLink.customer_name}` : ''}
            <div style={{ fontSize:11, marginTop:4, color:'var(--text-secondary)' }}>Go to <strong>Enquiry Tracker → {existingLink?.service_type} tab</strong> — this quote will auto-fill when you create the booking.</div>
          </div>
        ) : lifecycleStatus === 'done' ? (
          <div style={{ marginTop:12, padding:'10px 14px', background:'#D1FAE5', border:'1px solid #6EE7B7', borderRadius:8, fontSize:13, color:'#065F46' }}>
            ✓ {lifecycleMsg}
            <div style={{ fontSize:11, marginTop:4 }}>Go to <strong>Enquiry Tracker → {lifecycleRef?.startsWith('W')?'Wedding':lifecycleRef?.startsWith('BD')?'Birthday':lifecycleRef?.startsWith('E')?'Engagement':'applicable service'} tab</strong> to confirm and create a booking.</div>
          </div>
        ) : linkMode === 'search' ? (
          <div style={{ marginTop:12, padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#1E3A5F' }}>🔎 Link to Existing Enquiry</div>
              <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={() => setLinkMode('choose')}>← Back</button>
            </div>
            <input className="input" placeholder="Search by customer name or ref number..." value={enquirySearch}
              onChange={e => setEnquirySearch(e.target.value)} style={{ marginBottom:10 }} autoFocus />
            <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 }}>
              {SVCS.map(t => (
                <button key={t} onClick={() => openLinkSearch(t)}
                  style={{ padding:'4px 10px', borderRadius:100, fontSize:11.5, fontWeight: enqTab===t?700:500,
                    background: enqTab===t?'rgba(183,121,31,0.15)':'var(--bg-subtle)',
                    color: enqTab===t?'var(--gold)':'var(--text-secondary)',
                    border: enqTab===t?'1px solid rgba(183,121,31,0.3)':'1px solid transparent', cursor:'pointer' }}>
                  {t}
                </button>
              ))}
            </div>
            {enquiryLoading && <div style={{ fontSize:12, color:'var(--text-muted)' }}>Loading {enqTab} enquiries…</div>}
            {enquiryLoadError && <div style={{ fontSize:12, color:'#B91C1C' }}>{enquiryLoadError}</div>}
            {!enquiryLoading && !enquiryLoadError && (
              <div style={{ maxHeight:200, overflowY:'auto' }}>
                {enquiryList.filter(e =>
                  !enquirySearch ||
                  e.customer_name?.toLowerCase().includes(enquirySearch.toLowerCase()) ||
                  e.ref_number?.includes(enquirySearch)
                ).length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--text-muted)', padding:'8px 0' }}>No matching enquiries found.</div>
                ) : enquiryList.filter(e =>
                  !enquirySearch ||
                  e.customer_name?.toLowerCase().includes(enquirySearch.toLowerCase()) ||
                  e.ref_number?.includes(enquirySearch)
                ).map(e => (
                  <div key={e.id} onClick={() => handleLinkExisting(e)}
                    style={{ padding:'8px 10px', borderRadius:6, marginBottom:4, cursor:'pointer', border:'1px solid var(--border)', background:'#fff', fontSize:12.5 }}
                    onMouseEnter={ev=>(ev.currentTarget as HTMLDivElement).style.borderColor='var(--teal)'}
                    onMouseLeave={ev=>(ev.currentTarget as HTMLDivElement).style.borderColor='var(--border)'}>
                    <div style={{ fontWeight:600 }}>{e.customer_name || '(no name)'}</div>
                    <div style={{ color:'var(--text-muted)', fontSize:11 }}>{e.ref_number} · {e.service_type} · {e.event_dates || 'Date TBD'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop:12, padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#1E3A5F', marginBottom:4 }}>📋 Add to Lifecycle Tracker</div>
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:10 }}>
              This is a standalone quote. Either start a fresh enquiry for it, or — if the customer's enquiry was already logged separately — link this quote to that instead.
            </div>
            {lifecycleMsg && <div style={{ fontSize:12, color:'#B91C1C', marginBottom:8 }}>{lifecycleMsg}</div>}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button className="btn btn-primary btn-sm" onClick={handleAddToLifecycle} disabled={lifecycleStatus === 'loading'}
                style={{ fontSize:12 }}>
                {lifecycleStatus === 'loading' ? 'Creating enquiry…' : '🔗 Create New Enquiry'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => openLinkSearch()} disabled={lifecycleStatus === 'loading'}
                style={{ fontSize:12 }}>
                🔎 Link to Existing Enquiry
              </button>
            </div>
          </div>
        )}

        {onEdit && !isGP && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(quote); }}>
              Edit Full Quote
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
